// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpResponse } from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';

// Import services
import { LoginService } from './login.service';
import { ConfigService } from '../../config/config.service';
import { Observable, from, lastValueFrom, map, switchMap } from 'rxjs';

// Define types
type HttpOptions = {
    headers?: HttpHeaders | { [header: string]: string | string[] },
    observe?: 'body',
    params?: HttpParams | { [param: string]: string | string[] },
    reportProgress?: boolean,
    responseType?: string
};

class RequestError extends Error {
    public readonly url: string;
    public readonly method: string;
    public readonly body?: any;

    constructor(url: string, method: string, body?: any, error?: Error) {
        super();
        Object.assign(this, error);

        this.url = url;
        this.method = method;
        this.body = body;
    }
}

@Injectable({
    providedIn: 'root'
})
export class LoggedInHttpService {
    constructor(private config: ConfigService, private login: LoginService, private http: HttpClient) { }

    // New functions for state management
    //============================================================================================================================
    public _get<T>(path: string, options?: HttpOptions) {
        return from(this.finishOptions(options)).pipe(
            switchMap(
                options => this.http.get<T>(this.config.data.baseUri + path, options) as Observable<T>
            )
        )
    }

    public _post<T>(path: string, body: any, options?: HttpOptions) {
        return from(this.finishOptions(options)).pipe(
            switchMap(
                options => this.http.post<T>(this.config.data.baseUri + path, body, options) as Observable<T>
            )
        )
    }
    

    public _put<T>(path: string, body: any, options?: HttpOptions) {
        return from(this.finishOptions(options)).pipe(
            switchMap(
                options => this.http.put<T>(this.config.data.baseUri + path, body, options) as Observable<T>
            )
        )
    }

    public _delete<T>(path: string, options?: HttpOptions) {
        return from(this.finishOptions(options)).pipe(
            switchMap(
                options => this.http.delete<T>(this.config.data.baseUri + path, options) as Observable<T>
            )
        )
    }
    //============================================================================================================================

    public async get(path: string, options?: HttpOptions): Promise<any> {
        try {
            return await lastValueFrom(this.http.get(this.config.data.baseUri + path, await this.finishOptions(options)));
        } catch (err) {
            throw new RequestError(this.config.data.baseUri + path, 'GET', undefined, err);
        }
    }

    public async put(path: string, body: any, options?: HttpOptions): Promise<any> {
        try {
            return await lastValueFrom(this.http.put(this.config.data.baseUri + path, body, await this.finishOptions(options)));
        } catch (err) {
            throw new RequestError(this.config.data.baseUri + path, 'PUT', body, err);
        }
    }

    public async post<T>(path: string, body: any, options?: HttpOptions): Promise<T> {
        try {
            return (await lastValueFrom(this.http.post(this.config.data.baseUri + path, body, await this.finishOptions(options)))) as T;
        } catch (err) {
            throw new RequestError(this.config.data.baseUri + path, 'POST', body, err);
        }
    }

    public async delete(path: string, options?: HttpOptions): Promise<any> {
        try {
            return await lastValueFrom(this.http.delete(this.config.data.baseUri + path, await this.finishOptions(options)));
        } catch (err) {
            throw new RequestError(this.config.data.baseUri + path, 'DELETE', undefined, err);
        }
    }

    // Modifies the options to include the authorization header with the access token
    private async finishOptions(options: HttpOptions = {}): Promise<any> {
        // Retrieve token
        const token = await this.login.getToken();

        // Set up result
        const res = {
            headers: options.headers || {},
            params: options.params,
            reportProgress: options.reportProgress,
            observe: options.observe,
            responseType: options.responseType || 'json',
            withCredentials: false
        };

        // Add token to headers
        if (res.headers instanceof HttpHeaders) {
            if (res.headers.has('Authorization'))
                res.headers = res.headers.set('Authorization', `Bearer ${token}`)
            else
                res.headers = res.headers.append('Authorization', `Bearer ${token}`);
        } else
            res.headers['Authorization'] = `Bearer ${token}`;

        return res;
    }
}
